import { gql, useMutation, useReactiveVar } from "@apollo/client";
import { currentUserVar } from "apollo/cache/auth";
import { StatusBadge } from "components/core";
import {FC, ChangeEvent, useState} from "react";
import toast, {LoaderIcon} from "react-hot-toast";
import lodash from "lodash";
import { ClipboardDocumentListIcon, ArrowUpTrayIcon} from "@heroicons/react/24/outline";
import axios from "axios";
import config from "../../config";
import Loader from "../../components/layouts/loader";
import {FilePreview, ActionButton} from "../../components";
import {withRoles} from "../../utils";


axios.defaults.baseURL = config.asset.uri;

type Props = {
  data: any,
  loading: boolean,
  refetch: () => void,
}

export const UPDATE_DOCUMENTATION = gql`
  mutation UpdateMeterContractorDocumentation(
    $id: ID!
    $documentation: MeterContactorDocumentationInput
  ) {
    updateMeterContractorDocumentation(
      id: $id
      documentation: $documentation
    ) {
      _id
    }
  }
`;

const fileNameMap = {
  approvedTechnicalDocument: "Approved Technical Document",
  calibrationCertificate: "Calibration Certificate",
  typeTestReport: "Type Test Report",
  ISO14001: "ISO 14001: Environmental Management System Standard",
  ISO45001: "ISO 45001: Occupational Health and Safety Management System",
  ISO9001: "ISO 9001",
  schematicWiringDrawing: "Schematic Wiring Drawing",
  installationOperationalAndMaintenanceManuals: "Installation, Operation and Maintenance Manuals",
  detailedPartListAndMaterialsOfConstruction: "Detailed Parts List and Materials of Construction",
}

const SettingsDocumentationContainer: FC<Props> = ({ data, loading, refetch }) => {
  if(loading){
    return (
      <div className="flex-1 w-full min-h-full py-6">
        <Loader text={"Loading Documentation..."} />
      </div>
    )
  }


  return (
    <div className="overflow-hidden shadow-sm sm:rounded-md">
      <div className="bg-white px-8 py-5">
        <div className="rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="pl-3 pr-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Document Name
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-gray-200">
            {Object.keys(fileNameMap)
              .map(
                (fileKey) =>
                  <DocumentationItem
                    fileKey={fileKey}
                    document={lodash.get(data?.meterContractor?.documentation, fileKey)}
                    refetch={refetch}
                  />
              )
            }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


type DocumentationItemProps = {
  document: any,
  fileKey: string
  refetch: () => void;
}

const DocumentationItem = ({ document, refetch, fileKey }: DocumentationItemProps) => {

  const [uploadingFile, setUploadingFile] = useState(false)
  const currentUser = useReactiveVar(currentUserVar);
  const [updateDocumentation, { loading: loadingDocumentation }] = useMutation(UPDATE_DOCUMENTATION);

  let uploadStatus = !!document?.url ? "Complete" : "Incomplete"

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if(!file) return;

    const data = new FormData();
    data.append("file", file);

    setUploadingFile(true)
    await axios.post("/upload", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        }
      })
      .then(async ({ data }) => {
        await updateDocumentation({
          variables: {
            id: currentUser?.contractor?._id,
            documentation: {
              [fileKey]: data,
            }
          }
        }).then(({ data }) => {
          if (data.updateMeterContractorDocumentation._id) {
            refetch();
            toast(JSON.stringify({ type: "success", title: "Documentation Updated Successfully" }));
          }
          else {
            toast(JSON.stringify({ type: "error", title: "Could not update Documentation" }));
          }
        });
      })
      .catch((err) => {
        toast(JSON.stringify({ type: "error", title: "An error occurred while uploading the file" }))
      })
      .finally(() => setUploadingFile(false));
  };


  return (
    <tr key={fileKey} className="hover:bg-gray-50 transition-colors">

      <td className="flex-1 max-w-[370px] overflow-hidden pl-3 pr-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
        <ClipboardDocumentListIcon className={'text-gray-600 w-4 inline mr-2'} />
        <p className={"inline text-ellipsis"} title={fileNameMap[fileKey as keyof typeof fileNameMap]}>
          {fileNameMap[fileKey as keyof typeof fileNameMap]}
        </p>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge
          status={uploadStatus}
          options={{
            "Complete": "bg-green-100 text-green-600",
            "Incomplete": "bg-red-100 text-red-600",
          }}
        />
      </td>

      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {
          withRoles(["Super"])(
            <>
              <div className={"flex gap-4 justify-end"}>
                {uploadStatus === "Complete" &&
                  <FilePreview
                    url={document?.url}
                    trigger={({ setOpen }) => (
                      <div>
                        <ActionButton
                          action={'view'}
                          onClick={() => setOpen(true)}
                        />
                      </div>
                    )}
                  />
                }
                <label htmlFor={`file-upload-${fileKey}`} className="">
                  <span className={"h-[30px] w-[30px] cursor-pointer grid place-items-center aspect-square rounded-full hover:bg-gray-300 duration-150"}>
                    {
                      (loadingDocumentation || uploadingFile)
                        ? <LoaderIcon className={'text-gray-600 animate-spin w-4'} />
                        : <ArrowUpTrayIcon className={'text-gray-600 w-4'} />
                    }
                  </span>
                </label>
              </div>
              <input
                type="file"
                disabled={loadingDocumentation || uploadingFile}
                id={`file-upload-${fileKey}`}
                onChange={(e) => handleFileChange(e)}
                className="hidden"
              />
            </>
          )
        }
      </td>
    </tr>
  )

}


export default SettingsDocumentationContainer;